import React, { useState, useEffect } from 'react';
import { Layers, Clock, ArrowRightLeft } from 'lucide-react';
import { fetchMenuOptionUsage } from '../services/api';
import { useTelemetry } from '../context/TelemetryContext';

export default function MenuAnalyticsView(props) {
  const telemetry = useTelemetry();
  const mno = props.mno ?? telemetry.mno;
  const route = props.route ?? telemetry.route;
  const forceMock = props.forceMock ?? telemetry.forceMock;

  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMenuOptionUsage({ mno, route, forceMock }).then(data => {
      setOptions(data || []);
      setLoading(false);
    });
  }, [mno, route, forceMock]);

  const depthDistribution = [
    { hops: '1 Hop (Immediate Exit)', percentage: 4.2, count: 6220, color: '#94A3B8' },
    { hops: '2 Hops (Quick Statements)', percentage: 19.5, count: 28910, color: '#0F172A' },
    { hops: '3 Hops (Standard Balance/Pin)', percentage: 52.1, count: 77250, color: '#F84122' },
    { hops: '4 Hops (Airtime/Loans)', percentage: 14.8, count: 21940, color: '#2563EB' },
    { hops: '5+ Hops (Complex Transfers)', percentage: 9.4, count: 13940, color: '#10B981' },
  ];

  const screenDwellTimes = [
    { screen: 'MAIN_MENU (Dial Selection)', avgSeconds: 2.1, status: 'Fast (< 3s)' },
    { screen: 'PIN_ENTRY (Security Prompt)', avgSeconds: 4.8, status: 'Normal' },
    { screen: 'RECIPIENT_NUMBER (Phone Input)', avgSeconds: 7.2, status: 'Slow Input' },
    { screen: 'ENTER_AMOUNT (Numeric Amount)', avgSeconds: 5.6, status: 'Normal' },
    { screen: 'CONFIRMATION (Summary Review)', avgSeconds: 3.4, status: 'Fast (< 4s)' },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      
      {/* 1. Main Menu Distribution & Hop Depth */}
      <div className="two-col-grid">
        
        {/* Main Menu Option Breakdown */}
        <div className="glass-card" style={{ padding: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
            <Layers size={18} color="var(--accent-orange)" />
            <h2 style={{ fontSize: '16px', fontWeight: '800' }}>Root Menu Choices</h2>
          </div>
          <p style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '20px' }}>
            Subscriber choice shares when dialing primary shortcode (*185#, *165#)
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {loading ? (
              <div style={{ color: 'var(--text-muted)', fontSize: '13px', padding: '20px 0', textAlign: 'center' }}>
                Loading menu choices...
              </div>
            ) : options.length === 0 ? (
              <div style={{ color: 'var(--text-muted)', fontSize: '13px', padding: '20px 0', textAlign: 'center' }}>
                No menu choices recorded yet for the selected filter.
              </div>
            ) : (
              options.map((opt, i) => {
                const label = opt.optionSelected || opt.selectedOption || `Menu Option ${i + 1}`;
                const count = opt.selectionCount ?? opt.count ?? 0;
                const pct = opt.percentage ?? 0;
                const color = i === 0 ? '#F84122' : i === 1 ? '#0F172A' : i === 2 ? '#2563EB' : '#10B981';

                return (
                  <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '13px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <span style={{
                          width: '24px',
                          height: '24px',
                          borderRadius: '6px',
                          background: `${color}18`,
                          color: color,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '12px',
                          fontWeight: '700',
                          fontFamily: 'var(--font-mono)'
                        }}>
                          {i + 1}
                        </span>
                        <span style={{ fontWeight: '700', color: 'var(--text-primary)' }}>
                          {label}
                        </span>
                      </div>
                      <div>
                        <strong style={{ color: color, fontSize: '14px', fontWeight: '800' }}>{pct}%</strong>
                        <span style={{ color: 'var(--text-muted)', fontSize: '11px', marginLeft: '6px' }}>
                          ({count.toLocaleString()} sessions)
                        </span>
                      </div>
                    </div>
                    <div style={{ width: '100%', height: '8px', background: '#F1F5F9', borderRadius: '999px', overflow: 'hidden' }}>
                      <div style={{
                        width: `${Math.max(pct, 5)}%`,
                        height: '100%',
                        background: color,
                        borderRadius: '999px',
                        transition: 'width 0.6s ease'
                      }} />
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Interaction Depth Breakdown */}
        <div className="glass-card" style={{ padding: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
            <ArrowRightLeft size={18} color="var(--accent-orange)" />
            <h2 style={{ fontSize: '16px', fontWeight: '800' }}>Session Depth Distribution</h2>
          </div>
          <p style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '20px' }}>
            Breakdown of dialogues by total menu hops before termination
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            {depthDistribution.map((item, i) => (
              <div key={i}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '4px' }}>
                  <span style={{ color: 'var(--text-secondary)' }}>{item.hops}</span>
                  <strong style={{ color: 'var(--text-primary)' }}>{item.percentage}% ({item.count.toLocaleString()})</strong>
                </div>
                <div style={{ width: '100%', height: '7px', background: '#F1F5F9', borderRadius: '999px', overflow: 'hidden' }}>
                  <div style={{ width: `${item.percentage}%`, height: '100%', background: item.color, borderRadius: '999px' }} />
                </div>
              </div>
            ))}
          </div>
        </div>

      </div>

      {/* 2. Screen Dwell & User Thinking Latency Table */}
      <div className="glass-card" style={{ padding: '24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
          <Clock size={18} color="var(--accent-orange)" />
          <h2 style={{ fontSize: '16px', fontWeight: '800' }}>Screen Latency & Dwell Time Analysis</h2>
        </div>
        <p style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '20px' }}>
          Average subscriber hesitation and reading time before submitting inputs
        </p>

        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', textAlign: 'left' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid var(--border-medium)', color: 'var(--text-muted)' }}>
              <th style={{ padding: '10px 14px' }}>USSD Screen State</th>
              <th style={{ padding: '10px 14px' }}>Average Subscriber Dwell</th>
              <th style={{ padding: '10px 14px' }}>Hesitation Benchmark</th>
            </tr>
          </thead>
          <tbody>
            {screenDwellTimes.map((row, idx) => (
              <tr key={idx} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                <td style={{ padding: '12px 14px', fontWeight: '700', color: 'var(--text-primary)' }}>
                  {row.screen}
                </td>
                <td style={{ padding: '12px 14px', color: 'var(--text-primary)', fontFamily: 'var(--font-mono)', fontWeight: '600' }}>
                  {row.avgSeconds} seconds
                </td>
                <td style={{ padding: '12px 14px' }}>
                  <span style={{
                    fontSize: '11px',
                    fontWeight: '700',
                    color: row.status.includes('Slow') ? 'var(--accent-rose)' : 'var(--accent-emerald)',
                    background: row.status.includes('Slow') ? 'rgba(239, 68, 68, 0.1)' : 'rgba(16, 185, 129, 0.1)',
                    padding: '3px 8px',
                    borderRadius: '4px'
                  }}>
                    {row.status}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

    </div>
  );
}
